import React, { Component, createRef } from "react";
import { validateSearchFormParams } from "../utils.js";

class Search extends Component {
  constructor(props) {
    super(props);
    this.formErrorFocusRef = createRef();
    this.networkErrorFocusRef = createRef();

    this.focusOnError = this.focusOnError.bind(this);
    this.state = {
      majorName: "",
      applicantLevel: "",
      category: "",
      terms: {},
      campuses: {},
      showClosed: false,
      validationErrors: [],
      errorParams: {}
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      (prevProps.networkError !== this.props.networkError) ||
      (prevState.validationErrors !== this.state.validationErrors)
    ) {
      this.focusOnError();
    }
    // if we have just finished loading the options and we have inital params
    if (
      prevProps.isLoadingOptions &&
      !this.props.isLoadingOptions &&
      this.props.initialParams
    ) {
      // then set our state to match them
      const params = this.props.initialParams;
      this.setState({
        majorName: params.majorName || "",
        applicantLevel: params.applicantLevel || "",
        category: params.category || "",
        // we need to convert terms + campuses from something like [1, 3] to { 1: true, 3: true }

        terms: params.terms
          ? params.terms.reduce((terms, term) => {
              terms[term] = true;
              return terms;
            }, {})
          : {},
        campuses: params.campuses
          ? params.campuses.reduce((campuses, campus) => {
              campuses[campus] = true;
              return campuses;
            }, {})
          : {},
        showClosed: Boolean(params.showClosed)
      });
    }
  }

  focusOnError() {
    if (this.networkErrorFocusRef.current && this.props.networkError) {
      this.networkErrorFocusRef.current.focus();
      return;
    }
    if (this.formErrorFocusRef && this.state.validationErrors.length > 0) {
      this.formErrorFocusRef.current.focus();
      return;
    }
  }

  get shouldLockForm() {
    return this.props.isSearching || this.props.isLoadingOptions;
  }

  get allChecked() {
    const { campusOptions } = this.props.formOptions;
    return (
      !this.props.isLoadingOptions &&
      campusOptions.every(campus => this.state.campuses[campus.id])
    );
  }

  get selectedCampuses() {
    const { campuses } = this.state;
    return Object.keys(campuses).filter(campusId => campuses[campusId]);
  }

  get selectedTerms() {
    const { terms } = this.state;
    return Object.keys(terms).filter(termId => terms[termId]);
  }

  toggleTerm = termId => {
    this.setState({
      terms: {
        ...this.state.terms,
        [termId]: !this.state.terms[termId]
      }
    });
  };

  toggleCheckAllCampuses = () => {
    const campuses = {};

    if (!this.allChecked) {
      const { campusOptions } = this.props.formOptions;
      campusOptions.forEach(campus => (campuses[campus.id] = true));
    }

    this.setState({ campuses });
  };

  toggleCampus = campusId => {
    this.setState({
      campuses: {
        ...this.state.campuses,
        [campusId]: !this.state.campuses[campusId]
      }
    });
  };

  onSubmit = e => {
    e.preventDefault();
    const { applicantLevel, majorName, category, showClosed } = this.state;
    const formData = {
      campuses: this.selectedCampuses.map(id => parseInt(id)),
      terms: this.selectedTerms.map(id => parseInt(id)),
      majorName: majorName,
      showClosed: showClosed
    };

    if (applicantLevel) {
      formData.applicantLevel = parseInt(applicantLevel);
    }
    if (category) {
      formData.category = parseInt(category);
    }

    const validationErrors = validateSearchFormParams(formData);

    if (validationErrors.length > 0) {
      const errorParams = validationErrors.reduce((errorCollection, error) => {
        errorCollection[error.param] = true;
        return errorCollection;
      }, {});
      this.setState({ validationErrors, errorParams });
      return;
    } else {
      this.setState({
        validationErrors: [],
        errorParams: {}
      });
    }

    this.props.onSubmit(formData);
  };

  render() {
    const {
      campusOptions,
      applicantLevelOptions,
      termOptions,
      categoryOptions,
    } = this.props.formOptions;
    const { networkError } = this.props;
    const { validationErrors } = this.state;
    return (
      <div className="pb-5">
        <h2 className="h3">Check majors</h2>
        <span className="req-txt">* required</span>
        <hr className="hr-1 bg-blue my-4" />
        <span
          ref={this.networkErrorFocusRef}
          tabIndex={networkError ? "-1" : ""}
          className="errors"
        >{networkError && (<p>{networkError}</p>)}</span>
        <span
          ref={this.formErrorFocusRef}
          tabIndex={validationErrors.length > 0 ? "-1" : ""}
        >
          {validationErrors.length > 0 && (
            <ul className="errors">
              Before doing a search, you'll need to select:
              {validationErrors.map((err, index) => (
                <li key={index}>
                  <span>{err.message}</span>
                </li>
              ))}
            </ul>
          )}
        </span>
        <form onSubmit={this.onSubmit}>
          <div className="row mb-5 form-contents">
            <div className="col-md">
              <div className="row">
                <div className="col-lg-11">
                  <div className="form-group">
                    <label
                      className={
                        this.state.errorParams.major ? "err-highlight" : ""
                      }
                      htmlFor="major-name"
                    >
                      Major name
                    </label>
                    <input
                      id="major-name"
                      className="form-control"
                      placeholder="Enter major name"
                      name="major-name"
                      value={this.state.majorName}
                      onChange={e =>
                        this.setState({
                          majorName: e.target.value
                        })
                      }
                      disabled={this.shouldLockForm}
                    />
                  </div>

                  <div className="form-group">
                    <label
                      htmlFor="exampleSelect2"
                      className={
                        this.state.errorParams.applicantLevel
                          ? "err-highlight"
                          : ""
                      }
                    >
                      Applicant level<span className="sr_hide">required</span> *
                    </label>
                    <div className="custom-select-wrapper position-relative">
                      <select
                        id="exampleSelect2"
                        className="custom-select"
                        name="applicant-level"
                        value={this.state.applicantLevel}
                        onChange={e =>
                          this.setState({ applicantLevel: e.target.value })
                        }
                        disabled={this.shouldLockForm}
                      >
                        <option value="">--select--</option>
                        {applicantLevelOptions.map(level => (
                          <option key={level.id} value={level.id}>
                            {level.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="exampleSelect1">Category</label>
                    <div className="custom-select-wrapper position-relative">
                      <select
                        id="exampleSelect1"
                        className="custom-select"
                        name="category"
                        value={this.state.category}
                        onChange={e =>
                          this.setState({ category: e.target.value })
                        }
                        disabled={this.shouldLockForm}
                      >
                        <option value="">All categories</option>
                        {categoryOptions.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-2 term-block">
                <fieldset role="group" aria-labelledby="Term">
                  <h6
                    id="Term"
                    className={
                      this.state.errorParams.term
                        ? "err-highlight mb-3"
                        : "mb-3"
                    }
                  >
                    Term
                    <span className="sr_hide">
                      At least one term is required
                    </span>{" "}
                    *
                  </h6>
                  {termOptions.map(term => (
                    <div
                      key={term.id}
                      className="custom-control custom-checkbox custom-control-inline"
                    >
                      <input
                        type="checkbox"
                        id={term.name}
                        className="custom-control-input"
                        checked={Boolean(this.state.terms[term.id])}
                        onChange={() => this.toggleTerm(term.id)}
                        disabled={this.shouldLockForm}
                      />
                      <label
                        className="custom-control-label"
                        htmlFor={term.name}
                      >
                        {term.name}
                      </label>
                    </div>
                  ))}
                </fieldset>
              </div>
            </div>

            <div className="campus-column col-md">
              <fieldset role="group" aria-labelledby="campuses">
                <h6
                  className={
                    this.state.errorParams.campus
                      ? "err-highlight mt-1 mb-3"
                      : "mt-1 mb-3"
                  }
                  id="campuses"
                >
                  Campuses
                  <span className="sr_hide">
                    At least one Campus is required
                  </span>{" "}
                  *
                </h6>
                <div className="custom-control custom-checkbox mt-1 mb-5">
                  <input
                    type="checkbox"
                    data-campus-name="checkAll"
                    id="all-campuses"
                    checked={this.allChecked}
                    onChange={this.toggleCheckAllCampuses}
                    className="custom-control-input"
                    disabled={this.shouldLockForm}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="all-campuses"
                  >
                    All campuses
                  </label>
                </div>

                {campusOptions.map(campus => (
                  <div
                    className="custom-control custom-checkbox"
                    key={campus.id}
                  >
                    <input
                      id={`campus-${campus.id}`}
                      type="checkbox"
                      name="campuses"
                      checked={Boolean(this.state.campuses[campus.id])}
                      onChange={() => this.toggleCampus(campus.id)}
                      className="custom-control-input"
                      disabled={this.shouldLockForm}
                    />
                    <label
                      className="custom-control-label"
                      htmlFor={`campus-${campus.id}`}
                    >
                      {campus.name}
                    </label>
                  </div>
                ))}
              </fieldset>
            </div>

            <div className="closed-majors-column custom-control custom-checkbox show-closed">
              <input
                type="checkbox"
                id="singleCheckbox5"
                className="custom-control-input"
                checked={this.state.showClosed}
                onChange={() =>
                  this.setState({ showClosed: !this.state.showClosed })
                }
                disabled={this.shouldLockForm}
              />
              <label className="custom-control-label" htmlFor="singleCheckbox5">
                Show closed majors
              </label>
            </div>
          </div>

          <div className="row search-btn">
            <div className="col-md">
              <button
                className="btn btn-primary btn-blue btn-block"
                type="submit"
                disabled={this.shouldLockForm}
              >
                Search
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }
}
export default Search;
